import { useEffect, useState } from 'react'
import Seo from '../components/Seo'
import { Star, MapPin, Calendar, Wifi, UtensilsCrossed, Droplets, ChevronRight, ChevronLeft, Send, Check } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useFeaturedPackages } from '../hooks/useFeaturedPackages'
import { API_CONFIG } from '../config/api'

export default function Home() {
  const { franchiseData, loading } = useFranchiseData()
  const { featuredPackages, loading: packagesLoading, error: packagesError } = useFeaturedPackages()

  // Reviews data for carousel (real customer reviews)
  const reviews = [
    {
      name: 'Kish Ramdewu',
      location: 'December 5, 2024',
      rating: 5,
      quote:
        'Memento Travel was the most efficient choice I made, as I had zero admin to handle. Rene has got to be the best Rep, as she dealt with everything seamlessly and timeously. This was my first International travel, and it was all well planned. Thank you for the ease of it all and the excellent service. Appreciate it 🥺🥺'
    },
    {
      name: 'Salesh Singh',
      location: 'June 27, 2024',
      rating: 5,
      quote:
        'Memento Travel Co, arranged and planned my trip to Vietnam. it was perfectly planned. all transfers were seamless. Their constant contact and check in, whilst I was away, was the highlight for me. excellent company, great travel company. highly recommended. well done @Rene.'
    },
    {
      name: 'Hardus Mulder',
      location: 'April 8, 2024',
      rating: 5,
      quote:
        'We had an excellent experience with Rene. She arranged our flights and accommodation on a trip to Dubai and New Zealand. She was fast and courteous. She arranged our visas for Dubai, as well as a pick up and drop off at the airport which was excellent. She even did our online checkins and sent us the boarding passes. We literally just had to show up and everything else was taken care off. I would highly recommend using Memento Travel 😊'
    },
    {
      name: 'Nicole Carlse',
      location: 'July 19, 2024',
      rating: 5,
      quote:
        'Excellent service by Rene given in assisting with our flight booking, acknowledging messages at all hours, and helping with numerous itineraries. Highly recommend Memento Travel Co.'
    }
  ]

  // Form state management
  const [formData, setFormData] = useState({
    destination: '',
    duration: '1 week',
    groupDetails: '',
    preferredDates: '',
    budget: '',
    activities: '',
    email: '',
    whatsapp: '',
    additionalInfo: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<number>>(new Set())

  // Reviews carousel hooks (must be declared before any conditional return)
  const [activeReview, setActiveReview] = useState(0)
  useEffect(() => {
    const id = setInterval(() => {
      setActiveReview((prev) => (prev + 1) % reviews.length)
    }, 3000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews.length])

  const goPrev = () => setActiveReview((prev) => (prev - 1 + reviews.length) % reviews.length)
  const goNext = () => setActiveReview((prev) => (prev + 1) % reviews.length)

  const toggleDescription = (pkgId: number) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(pkgId)) {
        newSet.delete(pkgId)
      } else {
        newSet.add(pkgId)
      }
      return newSet
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      // Map home form data to contact form structure for API
      const message = `Destination: ${formData.destination}
Duration: ${formData.duration}
Group Details: ${formData.groupDetails}
Preferred Dates: ${formData.preferredDates}
Budget: ${formData.budget}
Activities: ${formData.activities}
WhatsApp: ${formData.whatsapp}
Additional Info: ${formData.additionalInfo}`

      const params = new URLSearchParams({
        'lead[first_name]': '', // Will be extracted from email or left empty
        'lead[last_name]': '', // Will be extracted from email or left empty
        'lead[email]': formData.email,
        'lead[telephone]': '', // Not captured in home form
        'lead[message]': message,
        'lead[franchise_id]': API_CONFIG.FRANCHISE_ID || '402',
        'lead[pax]': formData.groupDetails,
        'lead[dates]': formData.preferredDates,
        'lead[origin]': '' // Not captured in home form
      })

      const response = await fetch(`${API_CONFIG.BASE_URL}/leads?${params.toString()}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setSubmitSuccess(true)
        setFormData({
          destination: '',
          duration: '1 week',
          groupDetails: '',
          preferredDates: '',
          budget: '',
          activities: '',
          email: '',
          whatsapp: '',
          additionalInfo: ''
        })
        setTimeout(() => {
          setSubmitSuccess(false)
        }, 5000)
      } else {
        const errorData = await response.text()
        console.error('API Error:', response.status, errorData)
        throw new Error(`API Error: ${response.status}`)
      }
    } catch (error) {
      setSubmitError('Failed to send your message. Please try again.')
      console.error('Home form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Add debug logging
  console.log('Featured packages:', featuredPackages)
  console.log('Packages loading:', packagesLoading)
  console.log('Packages error:', packagesError)

  if (loading || !franchiseData) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    )
  }

  // Helper function similar to Packages.tsx
  const getImageUrl = (pkg: any) => {
    if (pkg.cover_photo?.large_url) {
      return pkg.cover_photo.large_url
    }
    if (pkg.cover_photo?.url) {
      return pkg.cover_photo.url
    }
    if (pkg.scraped_cover_photo) {
      return pkg.scraped_cover_photo
    }
    return null
  }

  const extractNightsFromName = (name: string): number | null => {
    if (!name) return null

    // Look for patterns like "2 nights", "3 night", etc.
    const nightsMatch = name.match(/(\d+)\s+nights?/i)
    if (nightsMatch) {
      return parseInt(nightsMatch[1], 10)
    }

    return null
  }

  const getNights = (pkg: any): number | null => {
    // Use the nights field if it exists and is not null
    if (pkg.nights !== null && pkg.nights !== undefined) {
      return pkg.nights
    }

    // Try to extract from name
    return extractNightsFromName(pkg.name)
  }

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === 'null') return ''

    try {
      // Handle various date formats
      const date = new Date(dateString)

      // Check if date is valid (not 1970 epoch)
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return ''
      }

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    } catch {
      return ''
    }
  }

  const getDates = (pkg: any): string => {
    // Debug logging
    console.log('Package date fields:', {
      dates: pkg.dates,
      travel_from: pkg.travel_from,
      travel_to: pkg.travel_to,
      valid_from: pkg.valid_from,
      valid_to: pkg.valid_to
    })

    // First try to use the dates field if it exists and is not null/empty
    if (pkg.dates && pkg.dates !== 'null' && pkg.dates !== null && pkg.dates.toString().trim() !== '') {
      return pkg.dates
    }

    // Then try travel_from and travel_to
    if (pkg.travel_from && pkg.travel_to &&
        pkg.travel_from !== 'null' && pkg.travel_to !== 'null' &&
        pkg.travel_from !== null && pkg.travel_to !== null) {
      const fromDate = formatDate(pkg.travel_from)
      const toDate = formatDate(pkg.travel_to)
      if (fromDate && toDate) {
        return `${fromDate} - ${toDate}`
      }
    }

    // Fall back to validity dates
    if (pkg.valid_from && pkg.valid_to &&
        pkg.valid_from !== 'null' && pkg.valid_to !== 'null' &&
        pkg.valid_from !== null && pkg.valid_to !== null) {
      const fromDate = formatDate(pkg.valid_from)
      const toDate = formatDate(pkg.valid_to)
      if (fromDate && toDate) {
        return `${fromDate} - ${toDate}`
      }
    }

    // Final fallback
    return 'Dates TBA'
  }

  return (
    <div className="bg-white">
      <Seo
        title="Memento Travel Co. – Personalized Travel & Holiday Packages"
        description="Enquire as clients, travel as our friends. Bespoke trips, curated packages, and expert guidance from globetrotting industry veterans."
        image="/Logo%20Design%20for%20Memento%20Travel%20white.png"
        canonicalUrl={typeof window !== 'undefined' ? window.location.origin + '/' : 'https://www.mementotravel.co/'}
        type="website"
        pageType="home"
      />
      {/* Hero section */}
      <div 
        className="relative isolate px-0 pt-14 lg:px-0 min-h-screen flex items-center justify-center overflow-hidden"
      >
         {/* Video Background */}
         <video
           autoPlay
           muted
           loop
           playsInline
           className="absolute inset-0 w-full h-full"
           style={{
             objectFit: 'cover',
             objectPosition: 'center',
             width: '100vw',
             height: '100vh',
             minWidth: '100%',
             minHeight: '100%'
          }}
        >
           <source src="https://firebasestorage.googleapis.com/v0/b/chelsea-travel-demo.firebasestorage.app/o/Untitled%20video%20-%20Made%20with%20Clipchamp.mp4?alt=media&token=e07c7f9b-243b-46a6-b50b-ecaec40d256b" type="video/mp4" />
         </video>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60"></div>
        {/* White Line Separator */}
        <div className="absolute top-20 left-0 right-0 h-px bg-white opacity-30"></div>

        {/* Award Recognition Section */}
        <div className="absolute top-40 left-1/2 transform -translate-x-1/2 text-center z-10">
          <img 
            src="/Logo%20Design%20for%20Memento%20Travel%20white.png" 
            alt="Memento Travel Logo" 
            className="h-32 w-auto mx-auto"
          />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl py-20 sm:py-32 lg:py-40 text-center px-6 lg:px-8">
          {/* <p className="text-lg text-white font-light tracking-widest uppercase mb-4">
            BEACHFRONT LUXURY ACCOMMODATION
          </p> */}
          <h1 className="text-2xl sm:text-4xl font-serif text-white leading-tight mb-4">
            Memento Travel Co.
          </h1>
          <p className="mt-4 text-base text-white font-light max-w-3xl mx-auto leading-relaxed">
            Enquire as clients, Travel as our friends
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#contact" className="text-white text-sm font-semibold px-5 py-2.5 rounded-none shadow-lg hover:opacity-90 transition-all duration-200" style={{background: '#006D77'}}>
              Start Planning
            </a>
            <a href="/packages" className="border-2 border-white text-white text-sm font-semibold px-5 py-2.5 rounded-none hover:bg-white hover:text-gray-900 transition-colors">
              Explore Packages
            </a>
          </div>
        </div>


        {/* Scroll Down Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center">
          <p className="text-white text-sm font-light mb-2">Scroll Down</p>
          <svg className="w-6 h-6 text-white mx-auto animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

      {/* Experience section (replaced with About content) */}
      <div className="py-24 sm:py-32" style={{background: '#006D77'}}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left text content */}
            <div>
              <p className="text-white/80 tracking-widest uppercase text-sm font-medium mb-4">
                Welcome to Memento Travel Co.
              </p>
              <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6">
              From Global Adventures to Your Dream Journey
            </h2>
              <p className="text-lg text-slate-200 leading-relaxed mb-6">
              Born from a passion ignited across 53 countries and two laps around the world, Memento Travel Co. transforms wanderlust into reality. Founded by cruise industry veteran Rene Traut, we bring global insights and heartfelt assistance to every adventure. Whether you're dreaming of luxury seaside escapes, cultural immersions, or thrilling new discoveries, we craft personalized journeys that turn your travel dreams into unforgettable memories. Experience the world through the eyes of someone who's truly lived it.
              </p>
              {/* <p className="text-lg text-slate-300 leading-relaxed">
                Whether you're seeking a romantic escape, a family vacation, or a
                peaceful retreat, our diverse range of accommodations caters to
                every preference and need. Each space is thoughtfully designed to
                provide panoramic sea views and direct beach access.
              </p> */}
              <div className="mt-8">
                <a href="/about" className="inline-flex items-center gap-2 text-white px-6 py-3 rounded-lg shadow hover:opacity-90 transition-all duration-200" style={{background: '#006D77'}}>
                  Learn More <span aria-hidden>→</span>
                </a>
            </div>
          </div>

            {/* Right image collage */}
            <div className="relative h-[520px]">
              {/* Large bottom image */}
              <img
                src="/Grace-Bay-Club-Infiniti-Bar.jpg"
                alt="Grace Bay Club Infiniti Bar"
                className="absolute inset-x-0 bottom-0 w-full h-80 object-cover rounded-3xl shadow-2xl"
              />
              {/* Top left image */}
              <img
                src="/607ee345965e389addb3c126_family-skiing-holiday.jpg"
                alt="Family skiing holiday"
                className="absolute -top-6 left-0 w-96 h-56 object-cover rounded-2xl shadow-xl"
              />
              {/* Top right image */}
              <img
                src="/Whitsundays-912741737119412.jpg"
                alt="Whitsundays destination"
                className="absolute -top-10 right-0 w-96 h-64 object-cover rounded-2xl shadow-xl"
              />
              {/* Bottom right image */}
              <img
                src="/petit-dejeuner-en-lune-de-miel-a-bora-bora-a-jeremy-austiin-1920x1080-f50_50.webp"
                alt="Bora Bora honeymoon breakfast"
                className="absolute right-6 bottom-4 w-80 h-56 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Featured Packages section */}
      <div id="offers" className="py-24 sm:py-32 bg-[#F7F7E9]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-[#006D77] tracking-widest uppercase text-sm font-medium">Our Accommodations</p>
            <h2 className="mt-2 text-4xl md:text-5xl font-serif text-gray-900">Featured Packages</h2>
            <p className="mt-4 text-gray-700 text-lg">
              Discover our most popular accommodation options, each offering a perfect blend of comfort,
              style, and breathtaking sea views.
            </p>
          </div>
          
          {packagesLoading ? (
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-start animate-pulse bg-slate-800/60 rounded-2xl overflow-hidden">
                  <div className="aspect-[16/9] w-full bg-slate-700"></div>
                  <div className="w-full p-6">
                    <div className="h-6 bg-slate-700 rounded w-2/3"></div>
                    <div className="mt-3 h-4 bg-slate-700 rounded w-1/3"></div>
                    <div className="mt-6 h-10 bg-slate-700 rounded w-28"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : packagesError ? (
            <div className="mx-auto mt-16 max-w-2xl text-center">
              <h3 className="text-lg font-semibold text-gray-900">Unable to load packages</h3>
              <p className="mt-2 text-sm text-gray-700">{packagesError}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 inline-flex items-center gap-2 text-white px-5 py-2 rounded-md transition"
                style={{background: '#006D77'}}
              >
                Try Again
              </button>
            </div>
          ) : featuredPackages.length === 0 ? (
            <div className="mx-auto mt-16 max-w-2xl text-center">
              <h3 className="text-lg font-semibold text-gray-900">No featured packages available</h3>
              <p className="mt-2 text-sm text-gray-700">Check back soon for our latest travel deals.</p>
              <a href="/packages" className="mt-4 inline-flex items-center gap-2 text-white px-5 py-2 rounded-md transition"
                style={{background: '#006D77'}}
              >
                View All Packages
              </a>
            </div>
          ) : (
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-14 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {featuredPackages.slice(0, 3).map((pkg) => {
                const imageUrl = getImageUrl(pkg)
                const nights = getNights(pkg)
                const nightsLabel = nights ? `${nights} ${nights === 1 ? 'Night' : 'Nights'}` : undefined
                const isExpanded = expandedDescriptions.has(pkg.id)
                const description = pkg.intro || pkg.description
                const shouldShowReadMore = description && description.length > 120

                return (
                  <article key={pkg.id} className="bg-white rounded-2xl overflow-hidden shadow-xl flex flex-col">
                    <div className="relative">
                      <div
                        className="aspect-[16/9] w-full object-cover"
                        style={{
                          backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-white font-medium text-lg drop-shadow">{pkg.name}</h3>
                        <div className="mt-1 text-slate-200 text-sm flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5" />
                          {pkg.location || pkg.destination?.name || 'International'}
                      </div>
                      </div>
                      {nightsLabel && (
                        <div className="absolute top-4 right-4 text-white px-3 py-1 rounded-full text-xs font-medium" style={{background: '#006D77'}}>
                          {nightsLabel}
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="min-h-[72px]">
                        <p className={`text-gray-600 text-sm leading-relaxed font-normal ${!isExpanded && shouldShowReadMore ? 'line-clamp-3' : ''}`}>
                          {description}
                        </p>
                        {shouldShowReadMore && (
                          <button
                            onClick={() => toggleDescription(pkg.id)}
                            className="mt-2 text-[#006D77] hover:text-[#004D5C] text-sm font-medium transition-colors"
                          >
                            {isExpanded ? 'Read less' : 'Read more'}
                          </button>
                        )}
                      </div>
                      <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4" style={{color: '#006D77', fill: '#006D77'}} />
                          {typeof pkg.rating === 'number' ? pkg.rating.toFixed(1) : (pkg.rating || 5)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {getDates(pkg)}
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-xs font-normal"><Wifi className="h-3.5 w-3.5" /> Wi‑Fi</span>
                        <span className="inline-flex items-center gap-1 text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-xs font-normal"><UtensilsCrossed className="h-3.5 w-3.5" /> Kitchen</span>
                        <span className="inline-flex items-center gap-1 text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-xs font-normal"><Droplets className="h-3.5 w-3.5" /> Bathroom</span>
                      </div>
                      <div className="mt-6 flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-gray-900 text-lg font-medium">
                            {pkg.formatted_price || `From R${(pkg.price_cents / 100).toLocaleString()} pps`}
                          </span>
                        </div>
                        <a
                          href={`/packages/${pkg.slug}`}
                          className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-md hover:opacity-90 transition-all duration-200 font-medium"
                          style={{background: '#006D77'}}
                        >
                          View Details <ChevronRight className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          )}
          <div className="mt-12 text-center">
            <a href="/packages" className="inline-flex items-center gap-2 text-white px-5 py-2 rounded-md hover:opacity-90 transition-all duration-200" style={{background: '#006D77'}}>
              View All Packages <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Booking CTA section (replaces Popular Destinations) */}
      <div id="contact" className="relative py-24 sm:py-32" style={{background: '#006D77'}}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            {/* Left copy */}
            <div className="lg:col-span-2">
              <p className="text-white/80 tracking-widest uppercase text-sm font-medium">Customise Your Trip</p>
              <h2 className="mt-2 text-4xl md:text-5xl font-serif text-white">Reserve Your Perfect Getaway</h2>
              <p className="mt-4 text-slate-200 text-lg max-w-xl">
                Take the first step towards your dream vacation by checking availability and securing your preferred dates.
                Our simple booking process ensures a seamless experience from reservation to arrival.
              </p>
              <ul className="mt-8 space-y-4 text-slate-200">
                {['Instant confirmation','Best rate guarantee','Flexible cancellation options','Secure payment'].map(item => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white">
                      <ChevronRight className="h-4 w-4" />
                    </span>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right form card */}
            <div className="lg:col-span-3 bg-white/95 border border-gray-200 rounded-2xl p-6 md:p-8 shadow-2xl backdrop-blur w-full">
              <h3 className="text-gray-900 font-serif text-2xl mb-6 text-center">Customise Your Trip</h3>
              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-medium text-green-600 mb-2">
                    Message Sent Successfully!
                  </h4>
                  <p className="text-slate-300">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Where are you looking to go? */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">Where are you looking to go?</label>
                  <p className="text-gray-600 text-sm mb-2">
                    Do you have a specific city, country, or region in mind? You can write
                    <span className="italic"> "not sure"</span> if that's part of why you're here :)
                  </p>
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    placeholder="eg. Greece or 'not sure'"
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  />
                </div>

                {/* How long are you traveling for? */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">How long are you traveling for?</label>
                  <p className="text-gray-600 text-sm mb-2">
                    You can give us an exact number of nights or just a general range.
                  </p>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  >
                    <option>Weekend (2–3 nights)</option>
                    <option>3–5 nights</option>
                    <option>1 week</option>
                    <option>10–14 nights</option>
                    <option>2 weeks</option>
                    <option>Longer (3+ weeks)</option>
                  </select>
                </div>

                {/* Who's coming with you? */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">Who's coming with you?</label>
                  <p className="text-gray-600 text-sm mb-2">
                    Let us know how many people are in your group and their ages, if possible.
                  </p>
                  <input
                    type="text"
                    name="groupDetails"
                    value={formData.groupDetails}
                    onChange={handleChange}
                    placeholder="eg. two six‑year olds, or no kids"
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  />
                </div>

                {/* Preferred travel dates */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">Do you have any preferred travel dates?</label>
                  <p className="text-gray-600 text-sm mb-2">
                    Let us know if your dates are locked in or if you're flexible.
                  </p>
                  <input
                    type="text"
                    name="preferredDates"
                    value={formData.preferredDates}
                    onChange={handleChange}
                    placeholder="eg. we have specific dates June 23 – 28"
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  />
                </div>

                {/* Specific budget */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">
                  What price range do we have to work with?
                  </label>
                  <p className="text-gray-600 text-sm mb-2">
                    Share a rough idea, either per night or for the whole trip.
                  </p>
                  <input
                    type="text"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="eg. R400 / night or R2,000 total"
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  />
                </div>

                {/* Anything specific to do */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">Anything specific you're looking to do?</label>
                  <p className="text-gray-600 text-sm mb-2">
                    Are there any experiences or bucket list activities you'd love to include?
                  </p>
                  <input
                    type="text"
                    name="activities"
                    value={formData.activities}
                    onChange={handleChange}
                    placeholder="eg. an attraction, historical site, tour, hike, restaurant or …"
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">What's your email address?</label>
                  <p className="text-gray-600 text-sm mb-2">
                    We'll use this to stay in touch and send over any trip details.
                  </p>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="eg. yourname@email.com"
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  />
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">What number can we use to chat with you via WhatsApp?</label>
                  <p className="text-gray-600 text-sm mb-2">
                    We'll use this to reach out via WhatsApp for quick updates and questions.
                  </p>
                  <input
                    type="text"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="eg. +27 82 123 4567"
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  />
                </div>

                {/* Anything else */}
                <div>
                  <label className="block text-gray-900 text-base mb-1">Anything else you'd like to share?</label>
                  <p className="text-gray-600 text-sm mb-2">
                    Maybe you're planning a family reunion, a local escape, or you prefer voice notes.
                    Whatever it is, we're all ears.
                  </p>
                  <textarea
                    rows={4}
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    placeholder="Type your answer here..."
                    className="w-full px-3 py-2 rounded-md bg-white text-gray-900 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#006D77]"
                  />
                </div>

                {submitError && (
                  <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-red-600 text-sm font-medium">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="md:col-span-2 w-full inline-flex items-center justify-center gap-2 text-white px-5 py-3 rounded-md hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{background: '#006D77'}}
                >
                  <Send className="h-4 w-4" />
                  {isSubmitting ? 'Sending...' : 'Submit'}
                </button>
              </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews carousel section (replaces Team) */}
      <div id="reviews" className="py-24 sm:py-32 bg-[#F7F7E9]">
        <div className="mx-auto max-w-5xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900">What Our Guests Say</h2>
          <p className="mt-3 text-gray-700 max-w-2xl mx-auto">
            Discover why our guests keep coming back to Memento for their seaside getaways.
          </p>

          <div className="relative mt-12">
            <button aria-label="Previous" onClick={goPrev} className="absolute -left-4 md:-left-10 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center justify-center">
              <ChevronLeft className="h-5 w-5" />
            </button>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 text-left shadow">
              <div>
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: reviews[activeReview].rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4" style={{color: '#006D77', fill: '#006D77'}} />
                  ))}
                </div>
                <p className="text-gray-700 italic text-lg">"{reviews[activeReview].quote}"</p>
                <div className="mt-6">
                  <div className="text-gray-900 font-medium text-lg">{reviews[activeReview].name}</div>
                  <div className="text-gray-500">{reviews[activeReview].location}</div>
                </div>
              </div>
            </div>

            <button aria-label="Next" onClick={goNext} className="absolute -right-4 md:-right-10 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center justify-center">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2">
            {reviews.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveReview(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                className={`h-2.5 w-2.5 rounded-full ${idx === activeReview ? '' : 'bg-gray-400'}`}
                style={idx === activeReview ? {background: '#006D77'} : {}}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
} 